import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash, HiPhotograph } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";

interface SliderItem {
  id: number;
  webImageUrl: string;
  mobileImageUrl: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
}

export const SliderIndex: React.FC = () => {
  const { t, i18n } = useTranslation(["common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [sliders, setSliders] = useState<SliderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const fetchSliders = async (page: number, size: number) => {
    try {
      setLoading(true);
      const response = await adminApi.call(`/slider?page=${page}&pageSize=${size}`, "GET");

      if (!response.success) {
        showErrorToast(response.error || "Failed to fetch sliders");
        return;
      }

      const items = response.data?.items || [];
      setSliders(items);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast("Error fetching sliders");
      console.error("Error fetching sliders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSliders(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const deleteSlider = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this slider?")) {
      return;
    }

    try {
      const response = await adminApi.call(`/slider/${id}`, "DELETE");

      if (!response.success) {
        showErrorToast(response.error || "Failed to delete slider");
        return;
      }

      showSuccessToast("Slider deleted successfully");
      fetchSliders(currentPage, pageSize);
    } catch (error) {
      showErrorToast("Error deleting slider");
      console.error("Error deleting slider:", error);
    }
  };

  const columns: Column[] = [
    {
      key: "displayOrder",
      title: "Order",
      sortable: true,
      width: "110px",
      align: "center"
    },
    {
      key: "webImageUrl",
      title: "Web Image",
      sortable: false,
      render: (value) => (
        <div className="slider-preview">
          <img src={value} alt="Web" style={{ width: "100px", borderRadius: "4px" }} />
        </div>
      )
    },
    {
      key: "mobileImageUrl",
      title: "Mobile Image",
      sortable: false,
      render: (value) => (
        <div className="slider-preview">
          <img src={value} alt="Mobile" style={{ width: "60px", borderRadius: "4px" }} />
        </div>
      )
    },
    {
      key: "isActive",
      title: "Status",
      sortable: true,
      width: "120px",
      align: "center",
      render: (value) => (
        <span className={`badge ${value ? "bg-success" : "bg-danger"}`}>
          {value ? "Active" : "Inactive"}
        </span>
      )
    },
    {
      key: "actions",
      title: "Actions",
      sortable: false,
      width: "150px",
      align: "center",
      render: (_, slider) => (
        <div className="action-buttons">
          {hasPermission("Update Sliders") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => navigate(`/cms/sliders/edit/${slider.id}`)}
              tooltip="Edit Slider"
            />
          )}
          {hasPermission("Delete Sliders") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteSlider(slider.id)}
              tooltip="Delete Slider"
            />
          )}
        </div>
      )
    }
  ];

  return (
    <PageLayout
      title="Sliders Management"
      subtitle="Manage your website hero sliders"
      breadcrumbs={[{ label: "CMS" }, { label: "Sliders" }]}
      className={isRTL ? "rtl" : "ltr"}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={sliders}
        loading={loading}
        searchable={false}
        filterable={false}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create Sliders") ? {
          label: "Add New Slider",
          onClick: () => navigate("/cms/sliders/create")
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onRefresh={() => fetchSliders(currentPage, pageSize)}
        emptyMessage="No sliders found"
      />
    </PageLayout>
  );
};
